import {Button, Col, Collapse, DatePicker, Divider, Drawer, Form, Input, InputNumber, Rate, Row, Select} from "antd";
import React, {useEffect, useState} from "react";
import BookCover from "@components/book-cover";
import {formatGrade, formatTitleCase} from "@lib/format";
import {InfoIcon} from "@components/icons/Icons";
import TextArea from "antd/es/input/TextArea";
import {
    useCollectionMarkAsSoldMutation,
    useGetShippingCompaniesQuery,
    useGetTransactionVenuesQuery
} from "@mwServices/User";
import {showResponse} from "@services/Api";
import ContactsSelect from "@components/contacts-select";
import useHasPermission from "@hooks/useHasPermission";
import {showInfoModal} from "@lib/showModal";
import {useWindowSize} from "@components/window-size-provider";

const { Panel } = Collapse;

interface MarkAsSoldDrawerProps {
    selectedIssues: any[];
    visible: boolean;
    onClose: () => void;
}

const defaultProps: MarkAsSoldDrawerProps = {
    selectedIssues: [],
    visible: false,
    onClose: () => {},
}

const MarkAsSoldDrawerContent = ({selectedIssues, form, onClose}) => {
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [callUpdate, updateResponse] = useCollectionMarkAsSoldMutation();
    const venuesQuery = useGetTransactionVenuesQuery();
    const [venues, setVenues] = useState<{ venue: string }[]>([]);
    const shippingQuery = useGetShippingCompaniesQuery();
    const [shippingCompanies, setShippingCompanies] = useState<{ shipping_co: string }[]>([]);

    useEffect(() => {
        if (venuesQuery.data) {
            setVenues(venuesQuery.data);
        }
    }, [venuesQuery.data]);
    useEffect(() => {
        if (shippingQuery.data) {
            setShippingCompanies(shippingQuery.data);
        }
    }, [shippingQuery.data]);

    useEffect(() => {
        showResponse(updateResponse, 'Collection item updated.', 'Failed to update the collection item.');
    }, [updateResponse]);

    const submitUpdates = async (values) => {
        const issue_ids = selectedIssues.map(issue => issue.collection_item_id);
        let sanitisedData = {
            issue_ids: issue_ids,
        };
        sanitisedData = {...sanitisedData, ...values};
        issue_ids.map(id => {
            if(values[`issue[${id}][sale_date]`]) sanitisedData[`issue[${id}][sale_date]`] = values[`issue[${id}][sale_date]`].format("YYYY-MM-DD");
        });

        await callUpdate(sanitisedData);

        form.resetFields();
        onClose();
    };

    useEffect(() => {
        form.resetFields();
    }, [selectedIssues]);

    return (
        <>
            <Row>
                <Col span={24}>
                    Items marked as sold will be removed from your collection. Soon you will be able to view and manage your sold items in the upcoming Insights section.
                </Col>
            </Row>

            <Form
                labelCol={{span: 12}}
                wrapperCol={{span: 12}}
                form={form}
                name="control-hooks"
                onFinish={submitUpdates}
            >
            <Row>
                <Col span={24}>
                    <Collapse ghost className="mark-as-sold-sections" defaultActiveKey={[0]} expandIconPosition={"end"}>
                        {selectedIssues.map((issue,i) => (
                            <Panel header={`${issue.series_title}: ${issue.issue_number} - ` + (issue.is_variant ? issue.variant_name : 'Regular')} key={i}>
                                <Row>
                                    <Col span={5}>
                                        <BookCover issue={issue} size={'small'} />
                                    </Col>
                                    <Col span={19} className={'align-left-top pad-left-10'}>
                                        <div>
                                            <b>{issue.series_title}: {issue.issue_number} - {issue.is_variant ? issue.variant_name : 'Regular'}</b><br/>
                                            <span className="issue-grade">{issue.graded_by ? issue.graded_by.toUpperCase() : issue.graded_or_raw.toUpperCase()} {formatGrade(issue.grade)}</span><br/>
                                            <span className="issue-note">{issue.notes}</span>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col span={4}  className={'align-left-top'}>
                                        <Button onClick={() =>{
                                            showInfoModal({
                                                title: "Grading Details",
                                                content: (
                                                    <>
                                                        <p>
                                                            These options allows you to enter specific information about the sale for your comic
                                                            so that you can keep it for your records. Items marked as sold will be archived in the
                                                            Profit/Loss section in your CovrPrice Collection. (NOTE: This section is currently not
                                                            available, but will be added soon)
                                                        </p>

                                                    </>
                                                ),
                                            });
                                        }} type="link" icon={<InfoIcon className="iconLarge"/>}/>
                                    </Col>
                                    <Col span={20}>
                                        <Form.Item label="Date Sold" name={`issue[${issue.collection_item_id}][sale_date]`}>
                                            <DatePicker
                                                className={'width-100'}
                                                format={(value) => `${value.format("YYYY/MM/DD")}`}
                                            />
                                        </Form.Item>

                                        <Form.Item label="Sale Amount" name={`issue[${issue.collection_item_id}][sale_price]`}>
                                            <InputNumber min={0} precision={2} addonBefore="$" placeholder="Enter Sale Amount"/>
                                        </Form.Item>
                                    </Col>
                                </Row>
                                {!showAdvanced && (
                                    <Row>
                                        <Col span={24} className='text-right'>
                                            <Button type="link" onClick={() => setShowAdvanced(true)} >+Advanced</Button>
                                        </Col>
                                    </Row>
                                )}
                                {showAdvanced && (
                                    <>
                                        <Row>
                                            <Col span={4}  className={'align-left-top'}>
                                            </Col>
                                            <Col span={20}>
                                                <Form.Item label="Additional Expense" name={`issue[${issue.collection_item_id}][additional_expenses]`}>
                                                    <InputNumber min={0} precision={2} addonBefore="$" placeholder=""/>
                                                </Form.Item>
                                                <Form.Item label="Sales Venue" name={`issue[${issue.collection_item_id}][venue]`}>
                                                    <Select
                                                        showSearch
                                                        onSearch={(value) => {
                                                            if (venuesQuery.data) {
                                                                setVenues([{venue: value}, ...venuesQuery.data]);
                                                            } else {
                                                                setVenues([{venue: value}]);
                                                            }
                                                            if(value && value !== '') form.setFieldValue(`issue[${issue.collection_item_id}][venue]`, value);
                                                        }}
                                                    >
                                                        <Select.Option value="">None</Select.Option>
                                                        {venues.map((venue, i) => (
                                                            <Select.Option key={i}
                                                                           value={venue.venue.toLowerCase()}>{formatTitleCase(venue.venue)}</Select.Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                                <Form.Item label="Sales Fees" name={`issue[${issue.collection_item_id}][fees]`}>
                                                    <InputNumber min={0} precision={2} addonBefore="$" placeholder=""/>
                                                </Form.Item>
                                                <Form.Item label="Transaction #" name={`issue[${issue.collection_item_id}][trx_id]`}>
                                                    <Input placeholder="" />
                                                </Form.Item>
                                                <Form.Item label="Sales Notes" name={`issue[${issue.collection_item_id}][notes]`}>
                                                    <TextArea rows={4} placeholder="" maxLength={1000} showCount/>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Divider />
                                        <Row>
                                            <Col span={4}  className={'align-left-top'}>
                                            </Col>
                                            <Col span={20}>
                                                <Form.Item label="Shipping Company" name={`issue[${issue.collection_item_id}][shipping_co]`}>
                                                    <Select
                                                        showSearch
                                                        onSearch={(value) => {
                                                            if (shippingQuery.data) {
                                                                setShippingCompanies([{shipping_co: value}, ...shippingQuery.data]);
                                                            } else {
                                                                setShippingCompanies([{shipping_co: value}]);
                                                            }
                                                            if(value && value !== '') form.setFieldValue(`issue[${issue.collection_item_id}][shipping_co]`, value);
                                                        }}
                                                    >
                                                        <Select.Option value="">None</Select.Option>
                                                        {shippingCompanies.map((shipping, i) => (
                                                            <Select.Option key={i}
                                                                           value={shipping.shipping_co.toLowerCase()}>{formatTitleCase(shipping.shipping_co)}</Select.Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                                <Form.Item label="Shipping Expense" name={`issue[${issue.collection_item_id}][shipping_fees]`}>
                                                    <InputNumber min={0} precision={2} addonBefore="$" placeholder=""/>
                                                </Form.Item>
                                                <Form.Item label="Tracking Number" name={`issue[${issue.collection_item_id}][tracking_number]`}>
                                                    <Input placeholder=""/>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Divider />
                                        <Row>
                                            <Col span={4}  className={'align-left-top'}>
                                            </Col>
                                            <Col span={20}>
                                                <ContactsSelect label="Buyer" name={`issue[${issue.collection_item_id}][contact_id]`} form={form} />

                                                <Form.Item label="Buyer Rating" name={`issue[${issue.collection_item_id}][contact_rating]`}>
                                                    <Rate />
                                                </Form.Item>
                                                <Form.Item label="Buyer Notes" name={`issue[${issue.collection_item_id}][contact_notes]`}>
                                                    <TextArea rows={4} placeholder="" maxLength={1000} showCount/>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </Panel>
                        ))}
                    </Collapse>
                </Col>
            </Row>
            </Form>
        </>
    );
};

const MarkAsSoldDrawer = ({selectedIssues, visible, onClose}: MarkAsSoldDrawerProps) => {
    const [form] = Form.useForm();
    const windowDimensions = useWindowSize();
    const [drawerWidth, setDrawerWidth] = useState(450);
    const isAdmin = useHasPermission('admin');

    useEffect(() => {
        const newWidth = (windowDimensions.width < 450) ? windowDimensions.width : 450;
        if(newWidth !== drawerWidth) setDrawerWidth(newWidth);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowDimensions]);

    const onCloseExec = () => {
        form.resetFields();
        onClose();
    };

    return (
        <Drawer
            title="Mark As Sold"
            className="mark-as-sold-drawer"
            headerStyle={{paddingTop: isAdmin ? 50 : 16}}
            width={drawerWidth}
            placement="right"
            onClose={onCloseExec}
            open={visible}
            footer={
                <Row justify="center">
                    <Col >
                        <Button className="outlineButton thickButton width-250px" onClick={() => form.submit()}>MARK AS SOLD</Button>
                    </Col>
                </Row>
            }
        >
            {selectedIssues && selectedIssues.length > 0 && (
                <MarkAsSoldDrawerContent selectedIssues={selectedIssues} onClose={onCloseExec} form={form} />
            )}
        </Drawer>
    );
};

MarkAsSoldDrawer.defaultProps = defaultProps;

export default MarkAsSoldDrawer;